import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  XCircle,
  Clock,
  ArrowRight,
  RefreshCw,
  Calendar,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import { getPaymentById, readLastPaymentId, readMercadoPagoInitPoint } from '@/services/payments';
import { createAppointment } from '@/services/appointments';
import { requestOnDemandConsultation } from '@/services/consultations';
import { formatBRL } from '@/lib/currency';
import { toast } from 'sonner';

type PaymentStatusLocal = 'approved' | 'rejected' | 'pending' | 'unknown';
type PendingFlow = 'appointment' | 'on_demand' | null;

const POLL_INTERVAL_MS = 3_000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

const APPOINTMENT_TYPE_BY_SERVICE: Record<string, 'TELEMEDICINA' | 'DOMICILIAR' | 'CONSULTORIO'> = {
  video: 'TELEMEDICINA',
  home: 'DOMICILIAR',
  clinic: 'CONSULTORIO',
};

function paymentLockKey(paymentId: string) {
  return `payment_finalize_lock_${paymentId}`;
}

export default function PaymentResultPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<PaymentStatusLocal>('unknown');
  const [amount, setAmount] = useState<number | null>(null);
  const [statusDetailLabel, setStatusDetailLabel] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);
  const [pollCountdown, setPollCountdown] = useState(0);
  const [initPoint, setInitPoint] = useState<string | null>(readMercadoPagoInitPoint());
  const [pendingFlow, setPendingFlow] = useState<PendingFlow>(null);
  const [finalizeError, setFinalizeError] = useState<string | null>(null);

  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const stopTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingRef = useRef(false);
  const finalizingRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollTimer.current) clearInterval(pollTimer.current);
    if (stopTimer.current) clearTimeout(stopTimer.current);
    pollingRef.current = false;
    setPolling(false);
  }, []);

  const resolvePaymentId = useCallback(() => {
    return searchParams.get('external_reference') || readLastPaymentId();
  }, [searchParams]);

  const finalizeOnDemand = useCallback(async (): Promise<boolean> => {
    const pendingStr = sessionStorage.getItem('pending_on_demand_triage');
    if (!pendingStr) return false;

    const pending = JSON.parse(pendingStr) as {
      paymentId: string;
      attendanceMode: 'TELEMEDICINE' | 'HOME_CARE';
      reason: string;
      symptoms?: string;
    };

    const lock = paymentLockKey(pending.paymentId);
    if (localStorage.getItem(lock)) return true;
    localStorage.setItem(lock, '1');

    const res = await requestOnDemandConsultation({
      paymentId: pending.paymentId,
      attendanceMode: pending.attendanceMode,
      reason: pending.reason,
      symptoms: pending.symptoms,
    });

    if (!res.success) {
      localStorage.removeItem(lock);
      throw new Error(res.error || 'Não foi possível entrar na fila de plantão.');
    }

    sessionStorage.removeItem('pending_on_demand_triage');
    sessionStorage.setItem(
      'onDemandTriage',
      JSON.stringify({
        reason: pending.reason,
        symptoms: pending.symptoms,
        mode: pending.attendanceMode,
      }),
    );
    toast.success('Pagamento confirmado. Entrando na fila de espera...');
    navigate('/on-demand/waiting');
    return true;
  }, [navigate]);

  const finalizePendingAppointment = useCallback(async (): Promise<boolean> => {
    const pendingApptStr = localStorage.getItem('pending_appointment');
    if (!pendingApptStr) return false;

    const pendingAppt = JSON.parse(pendingApptStr) as {
      paymentId: string;
      doctorId: string;
      serviceType: 'video' | 'home' | 'clinic';
      appointmentDate: string;
      appointmentTime: string;
      notes?: string;
    };

    const userDataStr = localStorage.getItem('user');
    let userId: string | undefined;
    if (userDataStr) {
      try {
        userId = JSON.parse(userDataStr).id;
      } catch {
        /* ignore */
      }
    }

    if (!userId || !pendingAppt.doctorId || !pendingAppt.appointmentDate || !pendingAppt.appointmentTime) {
      return false;
    }

    const lock = paymentLockKey(pendingAppt.paymentId);
    if (localStorage.getItem(lock)) {
      localStorage.removeItem('pending_appointment');
      return true;
    }
    localStorage.setItem(lock, '1');

    const res = await createAppointment({
      patientId: userId,
      doctorId: pendingAppt.doctorId,
      date: pendingAppt.appointmentDate,
      time: pendingAppt.appointmentTime,
      type: pendingAppt.serviceType,
      appointmentType: APPOINTMENT_TYPE_BY_SERVICE[pendingAppt.serviceType],
      notes: pendingAppt.notes,
      paymentId: pendingAppt.paymentId,
    });

    if (!res.success) {
      localStorage.removeItem(lock);
      throw new Error(res.error || 'Não foi possível confirmar o agendamento.');
    }

    localStorage.removeItem('pending_appointment');
    toast.success('Consulta agendada com sucesso!');
    setTimeout(() => navigate('/minhas-consultas'), 1200);
    return true;
  }, [navigate]);

  const continueAfterApproval = useCallback(async () => {
    if (finalizingRef.current) return;
    finalizingRef.current = true;
    setFinalizeError(null);

    try {
      if (sessionStorage.getItem('pending_on_demand_triage')) {
        setPendingFlow('on_demand');
        await finalizeOnDemand();
        return;
      }
      if (localStorage.getItem('pending_appointment')) {
        setPendingFlow('appointment');
        await finalizePendingAppointment();
        return;
      }
    } catch (err) {
      finalizingRef.current = false;
      const message = err instanceof Error ? err.message : 'Erro ao concluir após o pagamento.';
      setFinalizeError(message);
      toast.error(message);
    }
  }, [finalizeOnDemand, finalizePendingAppointment]);

  const fetchBackendStatus = useCallback(
    async (externalRef: string, mpPaymentId?: string | null): Promise<PaymentStatusLocal> => {
      try {
        const res = await getPaymentById(externalRef, mpPaymentId ?? undefined);
        if (res.success && res.data) {
          if (res.data.amount) setAmount(res.data.amount / 100);
          if (res.data.statusDetailLabel) setStatusDetailLabel(res.data.statusDetailLabel);
          if (res.data.initPoint) setInitPoint(res.data.initPoint);
          if (res.data.status === 'succeeded') return 'approved';
          if (res.data.status === 'failed') return 'rejected';
          return 'pending';
        }
      } catch {
        /* ignore */
      }
      return 'pending';
    },
    [],
  );

  const startPolling = useCallback(
    (externalRef: string, mpPaymentId?: string | null) => {
      if (pollingRef.current) return;
      pollingRef.current = true;
      setPolling(true);
      setPollCountdown(Math.floor(POLL_TIMEOUT_MS / 1000));

      const countdownTimer = setInterval(() => {
        setPollCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownTimer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      pollTimer.current = setInterval(async () => {
        const newStatus = await fetchBackendStatus(externalRef, mpPaymentId);
        if (newStatus === 'approved' || newStatus === 'rejected') {
          setStatus(newStatus);
          clearInterval(countdownTimer);
          stopPolling();
          if (newStatus === 'approved') await continueAfterApproval();
        }
      }, POLL_INTERVAL_MS);

      stopTimer.current = setTimeout(() => {
        clearInterval(countdownTimer);
        stopPolling();
      }, POLL_TIMEOUT_MS);
    },
    [fetchBackendStatus, continueAfterApproval, stopPolling],
  );

  useEffect(() => {
    async function processResult() {
      const paramStatus = searchParams.get('status') || searchParams.get('collection_status');
      const mpPaymentId = searchParams.get('payment_id') || searchParams.get('collection_id');
      const externalRef = resolvePaymentId();

      if (sessionStorage.getItem('pending_on_demand_triage')) setPendingFlow('on_demand');
      else if (localStorage.getItem('pending_appointment')) setPendingFlow('appointment');

      if (externalRef) {
        const backendStatus = await fetchBackendStatus(externalRef, mpPaymentId);
        setStatus(backendStatus);
        if (backendStatus === 'approved') {
          await continueAfterApproval();
        } else if (backendStatus === 'pending') {
          startPolling(externalRef, mpPaymentId);
        }
      } else {
        const urlStatus: PaymentStatusLocal =
          paramStatus === 'approved'
            ? 'approved'
            : paramStatus === 'rejected'
              ? 'rejected'
              : paramStatus === 'pending' || paramStatus === 'in_process'
                ? 'pending'
                : 'unknown';
        setStatus(urlStatus);
        if (urlStatus === 'approved') await continueAfterApproval();
      }

      setLoading(false);
    }

    processResult();
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const minutesLeft = Math.ceil(pollCountdown / 60);
  const pendingTitle =
    pendingFlow === 'on_demand'
      ? 'Aguardando o pagamento para entrar na fila'
      : pendingFlow === 'appointment'
        ? 'Aguardando o pagamento para confirmar o agendamento'
        : 'Aguardando confirmação do pagamento';

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-slate-900/90 backdrop-blur-xl border border-slate-800 rounded-3xl p-8 shadow-2xl relative overflow-hidden text-slate-100">

        {status === 'approved' && <div className="absolute -top-32 -left-32 w-64 h-64 bg-emerald-500/15 rounded-full blur-3xl pointer-events-none" />}
        {status === 'rejected' && <div className="absolute -top-32 -left-32 w-64 h-64 bg-rose-500/15 rounded-full blur-3xl pointer-events-none" />}
        {(status === 'pending' || status === 'unknown') && <div className="absolute -top-32 -left-32 w-64 h-64 bg-amber-500/15 rounded-full blur-3xl pointer-events-none" />}

        {loading ? (
          <div className="py-12 flex flex-col items-center justify-center space-y-4">
            <RefreshCw className="w-10 h-10 text-sky-400 animate-spin" />
            <p className="text-sm font-medium text-slate-300">Verificando confirmação do pagamento...</p>
          </div>

        ) : status === 'approved' ? (
          <div className="flex flex-col items-center text-center space-y-6">
            <div className="w-20 h-20 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shadow-lg shadow-emerald-500/20 animate-in zoom-in-75 duration-300">
              <CheckCircle2 className="w-10 h-10" />
            </div>
            <div>
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                Pagamento Aprovado
              </span>
              <h1 className="text-2xl font-bold text-slate-100 mt-3 tracking-tight">
                {pendingFlow === 'on_demand' ? 'Redirecionando para a fila de espera...' : 'Tudo pronto para a sua consulta!'}
              </h1>
              <p className="text-sm text-slate-400 mt-1 max-w-sm">
                {pendingFlow === 'on_demand'
                  ? 'Seu pagamento foi confirmado. Vamos te colocar na fila de plantão.'
                  : 'Seu pagamento foi confirmado e a consulta será agendada em seguida.'}
              </p>
            </div>
            {finalizeError && (
              <div className="w-full p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs leading-relaxed">
                {finalizeError}
              </div>
            )}
            {amount && (
              <div className="w-full bg-slate-800/50 border border-slate-700/50 rounded-2xl p-4 flex justify-between items-center text-sm">
                <span className="text-slate-400">Valor pago</span>
                <span className="font-bold text-emerald-400 text-lg">{formatBRL(amount)}</span>
              </div>
            )}
            <div className="w-full space-y-3 pt-2">
              <button
                onClick={() => navigate(pendingFlow === 'on_demand' ? '/on-demand/waiting' : '/minhas-consultas')}
                className="w-full py-3.5 px-6 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold transition-all duration-200 shadow-lg shadow-emerald-500/20 flex items-center justify-center gap-2"
              >
                <Calendar className="w-4 h-4" />
                <span>{pendingFlow === 'on_demand' ? 'Ir para a fila de espera' : 'Ver Minhas Consultas'}</span>
                <ArrowRight className="w-4 h-4 ml-1" />
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full py-3 px-6 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-medium transition-all"
              >
                Voltar ao Início
              </button>
            </div>
          </div>

        ) : status === 'pending' ? (
          <div className="flex flex-col items-center text-center space-y-6">
            <div className="w-20 h-20 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 shadow-lg shadow-amber-500/20">
              {polling ? <Loader2 className="w-10 h-10 animate-spin" /> : <Clock className="w-10 h-10" />}
            </div>
            <div>
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/20">
                Aguardando pagamento
              </span>
              <h1 className="text-2xl font-bold text-slate-100 mt-3 tracking-tight">
                {pendingTitle}
              </h1>
              <p className="text-sm text-slate-400 mt-1 max-w-sm">
                Conclua Pix ou cartão na aba do Mercado Pago. Esta tela atualiza sozinha quando o pagamento for aprovado — o Checkout Pro não redireciona após Pix.
              </p>
            </div>

            {polling && (
              <div className="w-full bg-slate-800/50 border border-amber-500/20 rounded-2xl p-4 space-y-2">
                <div className="flex items-center justify-center gap-2 text-amber-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm font-medium">Verificando pagamento automaticamente…</span>
                </div>
                {pollCountdown > 0 && (
                  <p className="text-xs text-slate-500">
                    Aguardando por mais {minutesLeft} {minutesLeft === 1 ? 'minuto' : 'minutos'}.
                  </p>
                )}
              </div>
            )}

            {initPoint && (
              <button
                onClick={() => window.open(initPoint, 'mercadopago_checkout')}
                className="w-full py-3.5 px-6 rounded-xl bg-sky-500 hover:bg-sky-600 text-slate-950 font-bold transition-all duration-200 shadow-lg shadow-sky-500/20 flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                <span>Abrir checkout do Mercado Pago</span>
              </button>
            )}

            <div className="w-full space-y-3 pt-2">
              <button
                onClick={() => navigate(pendingFlow === 'on_demand' ? '/on-demand/triage' : '/agendamento')}
                className="w-full py-3 px-6 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-medium transition-all"
              >
                Voltar
              </button>
            </div>
          </div>

        ) : (
          <div className="flex flex-col items-center text-center space-y-6">
            <div className="w-20 h-20 rounded-full bg-rose-500/10 border border-rose-500/30 flex items-center justify-center text-rose-400 shadow-lg shadow-rose-500/20">
              <XCircle className="w-10 h-10" />
            </div>
            <div>
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-rose-500/10 text-rose-400 border border-rose-500/20">
                Pagamento Não Concluído
              </span>
              <h1 className="text-2xl font-bold text-slate-100 mt-3 tracking-tight">
                Não foi possível aprovar o pagamento
              </h1>
              <p className="text-sm text-slate-400 mt-1 max-w-sm">
                {statusDetailLabel || 'O pagamento foi recusado ou cancelado. Nenhuma cobrança definitiva foi efetuada.'}
              </p>
            </div>
            <div className="w-full space-y-3 pt-2">
              <button
                onClick={() =>
                  navigate(pendingFlow === 'on_demand' ? '/on-demand/triage' : '/agendamento')
                }
                className="w-full py-3.5 px-6 rounded-xl bg-sky-500 hover:bg-sky-600 text-slate-950 font-bold transition-all duration-200 shadow-lg shadow-sky-500/20 flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Tentar Novamente</span>
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full py-3 px-6 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-medium transition-all"
              >
                Voltar ao Início
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
